import type { ExperimentImportMetadata, PredictionFlightsResponse, ResourceOptions, User } from "@/http/api-types";
import { getAudienceSelectOptions } from "@/lib/utils";
import { useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import type { ExperimentFormValues } from "./ExperimentForm";

type UseExperimentFormModelArgs = {
  users: User[] | undefined;
  audienceCampaignData: Parameters<typeof getAudienceSelectOptions>[0];
  predictionFlightsData: PredictionFlightsResponse | undefined;
};

export function useExperimentFormModel({
  users,
  audienceCampaignData,
  predictionFlightsData,
}: UseExperimentFormModelArgs) {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = useFormContext<ExperimentFormValues>();

  const [
    experimentType,
    audiences = [],
    campaigns = [],
    predictionFlights = [],
    externalLinks = [],
    importMetadata,
    repoBranchPairs = [],
  ] = useWatch({
    control,
    name: [
      "experimentType",
      "audiences",
      "campaigns",
      "predictionFlights",
      "externalLinks",
      "importMetadata",
      "repoBranchPairs",
    ],
  });

  const {
    fields: campaignFields,
    append: appendCampaign,
    remove: removeCampaign,
    replace: replaceCampaigns,
  } = useFieldArray({ control, name: "campaigns" });

  const {
    fields: predictionFlightFields,
    append: appendPredictionFlight,
    remove: removePredictionFlight,
    replace: replacePredictionFlights,
  } = useFieldArray({ control, name: "predictionFlights" });

  const {
    fields: externalLinkFields,
    append: appendExternalLink,
    remove: removeExternalLink,
  } = useFieldArray({ control, name: "externalLinks" });

  const {
    fields: repoBranchPairFields,
    append: appendRepoBranchPair,
    remove: removeRepoBranchPair,
  } = useFieldArray({ control, name: "repoBranchPairs" });

  const audienceOptionsRaw = getAudienceSelectOptions(audienceCampaignData);

  const audienceOptions = useMemo(() => {
    if (!audienceOptionsRaw) return [];
    return Object.keys(audienceOptionsRaw).map(audienceId => ({
      label: `${audienceId} - ${audienceOptionsRaw[audienceId].audience_name}`,
      value: audienceId,
    }));
  }, [audienceOptionsRaw]);

  const campaignOptions = useMemo(() => {
    if (audiences.length === 0 || !audienceOptionsRaw) return [];
    const seen = new Map<string, ResourceOptions>();
    audiences.forEach(audienceId => {
      audienceOptionsRaw[audienceId]?.campaigns.forEach(campaign => {
        seen.set(campaign.id, campaign);
      });
    });
    return Array.from(seen.values());
  }, [audiences, audienceOptionsRaw]);

  const organizationIds = useMemo(() => {
    if (audiences.length === 0 || !audienceOptionsRaw) return [];
    const seen = new Set<number>();
    audiences.forEach(audienceId => {
      const raw = audienceOptionsRaw[audienceId]?.organization_id;
      if (raw === undefined || raw === null) return;
      const parsed = typeof raw === "number" ? raw : parseInt(raw, 10);
      if (!Number.isNaN(parsed)) seen.add(parsed);
    });
    return Array.from(seen);
  }, [audiences, audienceOptionsRaw]);

  const predictionFlightOptions = useMemo(() => {
    const baseOptions =
      predictionFlightsData?.results.map(pf => ({
        label: `${pf.id} - ${pf.name}`,
        value: pf.id.toString(),
      })) || [];

    const importedOptions =
      (importMetadata as ExperimentImportMetadata | null)?.prediction_flights?.map(predictionFlight => ({
        label: `${predictionFlight.prediction_flight_id} - ${predictionFlight.name}`,
        value: predictionFlight.prediction_flight_id.toString(),
      })) || [];

    const deduped = new Map<string, { label: string; value: string }>();
    [...baseOptions, ...importedOptions].forEach(option => {
      deduped.set(option.value, option);
    });

    return Array.from(deduped.values()).sort((left, right) => parseInt(left.value, 10) - parseInt(right.value, 10));
  }, [importMetadata, predictionFlightsData]);

  const userOptions = useMemo(
    () =>
      users?.map(user => ({
        label: user.name,
        value: user.id.toString(),
      })) || [],
    [users],
  );

  const totalCampaignTrafficAllocation = useMemo(
    () => campaigns.reduce((sum, c) => sum + (c.trafficAllocation || 0), 0),
    [campaigns],
  );

  return {
    control,
    setValue,
    handleSubmit,
    isSubmitting,
    isDirty,
    experimentType,
    audiences,
    campaigns,
    predictionFlights,
    externalLinks,
    importMetadata,
    campaignFields,
    appendCampaign,
    removeCampaign,
    replaceCampaigns,
    predictionFlightFields,
    appendPredictionFlight,
    removePredictionFlight,
    replacePredictionFlights,
    externalLinkFields,
    appendExternalLink,
    removeExternalLink,
    repoBranchPairs,
    repoBranchPairFields,
    appendRepoBranchPair,
    removeRepoBranchPair,
    audienceOptions,
    audienceOptionsRaw,
    campaignOptions,
    organizationIds,
    predictionFlightOptions,
    userOptions,
    totalCampaignTrafficAllocation,
  };
}

export type ExperimentFormModel = ReturnType<typeof useExperimentFormModel>;
